import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from 'react';
import { useRecoilState } from 'recoil';
import dayjs from 'dayjs';
import { debounce } from 'lodash';

import { useRespondCalendarEvent, useUpdateCalendarEventEnergy } from '../mutations';
import { useAuthContext } from './AuthProvider';
import { eventsSnackBarAtom } from '../recoil';
import { RBCEvent } from '../../components';
import { CalendarEventModifyOption, EventDetailsMenuMode } from '../types';
import { useCalendarContext } from './CalendarProvider';
import { useAIScheduler } from '../../hooks/useAIScheduler';
import { useMetricsActivitiesInCalendar } from '../../hooks/useMetricsActivitiesInCalendar';
import {
  isTemporalAutoScheduledEvent,
  isAISuggestedEvent,
  isTemporaryEventMenuEvent,
} from '../../helpers';
import { transformRBCEventToCalendarEvent } from '../../components/CalendarView/CalendarTimeline/helpers';
import {
  CalendarEvent,
  CalendarEventAttendeeResponseStatus,
  CalendarEventMetrics,
} from '@demind-inc/core';
import { trackEventMixpanel } from '../../utils';
import { transformAllDayEventTime } from '../helpers';
import { useCircadianContext } from './CircadianProvider';
import { CreateCalendarEventParams, useEventActions } from '../hooks/useEventActions';
import { translateI18n } from '../../i18n';

interface IEventDetailsMenuContext {
  selectedEvent?: RBCEvent;
  /** Viewport rect of the clicked `.rbc-event` when opening the temporary preview menu. */
  selectedEventAnchorRect?: { top: number; left: number; right: number; bottom: number };
  visibleMenuMode?: EventDetailsMenuMode;
  forceEnableToSave?: boolean;
  aiSuggestedEvent?: Partial<CalendarEvent>;
  isAISuggestedEventModalVisible: boolean;
  handleAISuggestedEventModalClose: (shouldResetEvent?: boolean) => void;
  openAISuggestedEventModal: (event: Partial<CalendarEvent>) => void;
  patchAISuggestedEvent: (
    patch:
      | Partial<CalendarEvent>
      | ((prev: Partial<CalendarEvent> | undefined) => Partial<CalendarEvent> | undefined)
  ) => void;
  handleSaveEvent: (
    newEvent: CalendarEventModifyOption,
    options?: { recurringUpdateSpan?: 'thisEvent' | 'allEvents'; originalOccurrenceDate?: string }
  ) => void;
  handleRespondToEvent: (
    responseStatus: Extract<
      CalendarEventAttendeeResponseStatus,
      'accepted' | 'tentative' | 'declined'
    >
  ) => Promise<boolean>;
  isRespondingToEvent: boolean;
  handleDeleteEvent: (
    event: RBCEvent,
    options?: { recurringDeleteSpan?: 'thisEvent' | 'allEvents' }
  ) => void;
  handleSelectEvent: (event: RBCEvent, nativeEvent?: Event | null) => void;
  handleStartEditingEvent: () => void;
  handleEnergySave: (eventMetric: CalendarEventMetrics) => void;
  clearSelectedEvent: () => void;
  /** Keep the open temporary menu in sync after editing preview start/end on the calendar data layer. */
  patchSelectedTemporaryEventPreview: (patch: {
    start: Date;
    end: Date;
    eventId?: string;
    autoScheduleFromId?: string;
  }) => void;
}

export const EventDetailsMenuContext = createContext({} as IEventDetailsMenuContext);
export const useEventDetailsMenuContext = () => useContext(EventDetailsMenuContext);

type SupportedEventResponseStatus = Extract<
  CalendarEventAttendeeResponseStatus,
  'accepted' | 'tentative' | 'declined'
>;

export const EventDetailsMenuProvider = ({ children }: { children: ReactNode }) => {
  const [selectedEvent, setSelectedEvent] = useState<RBCEvent>();
  const [selectedEventAnchorRect, setSelectedEventAnchorRect] = useState<
    { top: number; left: number; right: number; bottom: number } | undefined
  >();
  const [visibleMenuMode, setVisibleMenuMode] = useState<EventDetailsMenuMode | undefined>();
  const [forceEnableToSave, setForceEnableToSave] = useState(false);
  const deletedEventRef = useRef<RBCEvent | null>(null);
  const shouldShowDeleteUndoRef = useRef(false);
  const [aiSuggestedEvent, setAiSuggestedEvent] = useState<Partial<CalendarEvent>>();
  const [isAISuggestedEventModalVisible, setIsAISuggestedEventModalVisible] = useState(false);

  const { handleAcceptAIScheduleEvent, handleDiscardEvent: handleDiscardAIScheduledEvent } =
    useAIScheduler();
  const { handleSelectActivityEvent } = useMetricsActivitiesInCalendar();
  const [_, setEventsSnackbar] = useRecoilState(eventsSnackBarAtom);
  const { user } = useAuthContext();
  const { updateCalendarEvent, createCalendarEvent, deleteCalendarEvent, eventDeletingStatus } =
    useEventActions();
  const {
    mainCalendar,
    calendarAccountsToCreateEvents,
    findCalendarItem,
    checkCalendarHassAccessToModifyEvent,
  } = useCalendarContext();
  const { updateCalendarEventEnergy } = useUpdateCalendarEventEnergy();
  const { respondCalendarEvent, isResponding: isRespondingToEvent } = useRespondCalendarEvent();
  const { findPhaseForTaskTime } = useCircadianContext();

  const handleAISuggestedEventModalClose = (shouldResetEvent = true) => {
    setIsAISuggestedEventModalVisible(false);
    if (shouldResetEvent) {
      setAiSuggestedEvent(undefined);
    }
  };

  const openAISuggestedEventModal = useCallback((event: Partial<CalendarEvent>) => {
    setAiSuggestedEvent(event);
    setIsAISuggestedEventModalVisible(true);
  }, []);

  const patchAISuggestedEvent = useCallback(
    (
      patch:
        | Partial<CalendarEvent>
        | ((prev: Partial<CalendarEvent> | undefined) => Partial<CalendarEvent> | undefined)
    ) => {
      setAiSuggestedEvent((prev) => {
        if (typeof patch === 'function') {
          return patch(prev);
        }
        return prev ? { ...prev, ...patch } : prev;
      });
    },
    []
  );

  const handleUndo = useCallback(() => {
    if (deletedEventRef.current) {
      const event = deletedEventRef.current;
      const calendarEvent = transformRBCEventToCalendarEvent(event);

      debouncedCreateCalendarEvent({
        calendarId: event.calendarId,
        newEventOption: {
          ...calendarEvent,
          eventId: null,
        },
      });

      deletedEventRef.current = null;
    }
  }, [user.userId, mainCalendar?.calendarId]);

  const debouncedCreateCalendarEvent = useCallback(
    debounce((createCalendarArgs: CreateCalendarEventParams) => {
      createCalendarEvent(createCalendarArgs);
    }, 2000),
    [createCalendarEvent, findPhaseForTaskTime]
  );

  useEffect(() => {
    if (eventDeletingStatus === 'success') {
      if (shouldShowDeleteUndoRef.current && deletedEventRef.current) {
        setEventsSnackbar({
          message: translateI18n('calendar.snackbar.eventDeleted'),
          actionLabel: translateI18n('calendar.common.undo'),
          onPress: handleUndo,
          autoHideDuration: 4000,
          type: 'success',
        });
      } else {
        setEventsSnackbar({ message: translateI18n('calendar.snackbar.eventDeleted'), type: 'success' });
      }
      shouldShowDeleteUndoRef.current = false;
    } else if (eventDeletingStatus === 'pending') {
      setEventsSnackbar({ message: translateI18n('calendar.snackbar.eventDeleting'), type: 'loading' });
    } else if (eventDeletingStatus === 'error') {
      setEventsSnackbar({ message: translateI18n('calendar.snackbar.deleteFailed'), type: 'error' });
      shouldShowDeleteUndoRef.current = false;
    }
  }, [eventDeletingStatus, handleUndo]);

  const handleDeleteEvent = (
    event: RBCEvent,
    options?: { recurringDeleteSpan?: 'thisEvent' | 'allEvents' }
  ) => {
    if (!event.calendarId) {
      return;
    }

    const hasCalendarAccess = checkCalendarHassAccessToModifyEvent(event.calendarId);
    if (!hasCalendarAccess) {
      setEventsSnackbar({ message: translateI18n('calendar.snackbar.noModifyAccess'), type: 'warning' });
      return;
    }

    if (isTemporalAutoScheduledEvent(event.eventId, ['aiScheduler'])) {
      if (event.autoScheduleFrom?.id) {
        handleDiscardAIScheduledEvent(event.autoScheduleFrom.id);
        clearSelectedEvent();
        return;
      }
    }

    const isAppleCalendarEvent = findCalendarItem(event.calendarId)?.calendarType === 'apple';
    shouldShowDeleteUndoRef.current = !isAppleCalendarEvent;
    deletedEventRef.current = isAppleCalendarEvent ? null : event;
    void deleteCalendarEvent({
      calendarId: event.calendarId,
      event: transformRBCEventToCalendarEvent(event),
      unSyncToCalendar: event.unSyncToCalendar,
      recurringDeleteSpan: options?.recurringDeleteSpan,
    });
    clearSelectedEvent();
  };

  const handleSelectEvent = (event: RBCEvent, nativeEvent?: Event | null) => {
    //@ts-ignore
    if (event.eventType === 'background') {
      // Disable interaction with background events
      return;
    }
    // Replan-my-day previews use `from: todo | routine` like chat suggestions, but must open
    // TemporaryEventMenu instead of the AI suggested-event sheet.
    if (isAISuggestedEvent(event?.autoScheduleFrom) && !event.isReplanPreview) {
      setAiSuggestedEvent(transformRBCEventToCalendarEvent(event));
      setIsAISuggestedEventModalVisible(true);
      return;
    }

    setForceEnableToSave(false);
    if (isTemporalAutoScheduledEvent(event?.eventId, ['activity'])) {
      setForceEnableToSave(true); // Temporal activity (metrics) previews
    }
    if (isTemporalAutoScheduledEvent(event?.eventId, ['activity'])) {
      setVisibleMenuMode('activity');
      handleSelectActivityEvent(event.eventId);
      return;
    }
    if (isTemporaryEventMenuEvent(event)) {
      const t = nativeEvent?.target;
      const clicked = t instanceof Element ? t : null;
      const eventElement = clicked?.closest('.rbc-event');
      const anchorRect = eventElement?.getBoundingClientRect();
      setSelectedEventAnchorRect(
        anchorRect
          ? {
              top: anchorRect.top,
              left: anchorRect.left,
              right: anchorRect.right,
              bottom: anchorRect.bottom,
            }
          : undefined
      );
      setSelectedEvent(event);
      setVisibleMenuMode('temporary');
      return;
    }
    setSelectedEventAnchorRect(undefined);
    setSelectedEvent(event);
    setVisibleMenuMode('edit');
  };

  const handleSaveEvent = (
    newEvent: CalendarEventModifyOption,
    options?: { recurringUpdateSpan?: 'thisEvent' | 'allEvents'; originalOccurrenceDate?: string }
  ) => {
    if (!selectedEvent) {
      return;
    }

    if (isTemporalAutoScheduledEvent(selectedEvent.eventId, ['aiScheduler'])) {
      void handleAcceptAIScheduleEvent(newEvent);
      clearSelectedEvent();
      return;
    }

    const hasCalendarAccess =
      !!selectedEvent.calendarId && checkCalendarHassAccessToModifyEvent(selectedEvent.calendarId);

    if (!hasCalendarAccess) {
      setEventsSnackbar({ message: translateI18n('calendar.snackbar.noModifyAccess'), type: 'warning' });
      return;
    }

    updateCalendarEvent({
      eventId: selectedEvent.eventId,
      calendarId: selectedEvent.calendarId,
      existingEvent: transformRBCEventToCalendarEvent(selectedEvent),
      recurringUpdateSpan: options?.recurringUpdateSpan,
      originalOccurrenceDate: options?.originalOccurrenceDate,
      newEventOption: {
        ...newEvent,
        ...(newEvent.isAllDay
          ? { ...transformAllDayEventTime({ start: newEvent.start, end: newEvent.end }) }
          : {}),
      },
    });
    clearSelectedEvent();
  };

  const handleRespondToEvent = async (
    responseStatus: SupportedEventResponseStatus
  ): Promise<boolean> => {
    if (!selectedEvent?.calendarId || !selectedEvent?.eventId) {
      return false;
    }

    const targetCalendarId = selectedEvent.calendarId;
    const targetEventId = selectedEvent.eventId;
    const hasCalendarAccess = checkCalendarHassAccessToModifyEvent(targetCalendarId);
    if (!hasCalendarAccess) {
      setEventsSnackbar({ message: translateI18n('calendar.snackbar.noModifyAccess'), type: 'warning' });
      return false;
    }

    try {
      await respondCalendarEvent({
        userId: user.userId,
        calendarId: targetCalendarId,
        eventId: targetEventId,
        responseStatus,
      });

      const selectedCalendar = findCalendarItem(targetCalendarId);
      const currentUserEmails = [
        selectedCalendar?.internalGCalendarId,
        selectedCalendar?.rootEmail,
        user.email,
      ].filter((email): email is string => !!email);

      setSelectedEvent((prevEvent) => {
        if (!prevEvent) {
          return prevEvent;
        }

        if (prevEvent.eventId !== targetEventId || prevEvent.calendarId !== targetCalendarId) {
          return prevEvent;
        }

        if (!prevEvent.attendees?.length) {
          return prevEvent;
        }

        let hasUpdatedAttendee = false;
        const attendees = prevEvent.attendees.map((attendee) => {
          if (currentUserEmails.includes(attendee.email)) {
            hasUpdatedAttendee = true;
            return { ...attendee, responseStatus };
          }

          return attendee;
        });

        if (!hasUpdatedAttendee) {
          return prevEvent;
        }

        return {
          ...prevEvent,
          attendees,
          isDeclined: responseStatus === 'declined',
          isAwaitingResponse: false,
        };
      });
      return true;
    } catch (error) {
      setEventsSnackbar({ message: translateI18n('calendar.snackbar.responseUpdateFailed'), type: 'error' });
      return false;
    }
  };

  const handleEnergySave = async (eventMetrics: CalendarEventMetrics) => {
    if (!selectedEvent?.eventId || ['temporal', 'creating'].includes(selectedEvent.eventId)) {
      return;
    }
    updateCalendarEventEnergy({
      calendarId: selectedEvent.calendarId ?? mainCalendar?.calendarId!,
      eventId: selectedEvent.eventId,
      eventMetrics: eventMetrics!,
      mutateParams: {
        date: dayjs(selectedEvent.start).format('YYYY-MM-DD'),
      },
    });
    trackEventMixpanel('answer_energy_feedback', {
      from: 'event_sheet',
      energyMetrics: eventMetrics,
    });
  };

  const handleStartEditingEvent = () => {
    setVisibleMenuMode('edit');
  };

  const clearSelectedEvent = () => {
    setSelectedEvent(undefined);
    setSelectedEventAnchorRect(undefined);
    setVisibleMenuMode(undefined);
    setAiSuggestedEvent(undefined);
    setIsAISuggestedEventModalVisible(false);
  };

  const patchSelectedTemporaryEventPreview = useCallback(
    (patch: { start: Date; end: Date; eventId?: string; autoScheduleFromId?: string }) => {
      setSelectedEvent((prev) => {
        if (!prev) {
          return prev;
        }
        return {
          ...prev,
          start: patch.start,
          end: patch.end,
          ...(patch.eventId ? { eventId: patch.eventId } : {}),
          ...(patch.autoScheduleFromId
            ? {
                autoScheduleFrom: {
                  ...prev.autoScheduleFrom,
                  id: patch.autoScheduleFromId,
                },
              }
            : {}),
        };
      });
    },
    []
  );

  return (
    <EventDetailsMenuContext.Provider
      value={{
        selectedEvent,
        selectedEventAnchorRect,
        visibleMenuMode,
        forceEnableToSave,
        aiSuggestedEvent,
        isAISuggestedEventModalVisible,
        handleAISuggestedEventModalClose,
        openAISuggestedEventModal,
        patchAISuggestedEvent,
        handleSaveEvent,
        handleRespondToEvent,
        isRespondingToEvent,
        handleSelectEvent,
        handleDeleteEvent,
        clearSelectedEvent,
        handleEnergySave,
        handleStartEditingEvent,
        patchSelectedTemporaryEventPreview,
      }}
    >
      {children}
    </EventDetailsMenuContext.Provider>
  );
};
